import {
  applicationIncomeApi,
  applicationLiabilityApi,
  applicationPropertyApi,
  financialInfoApi,
  loanApplicationBorrowersApi,
  loanPropertyApi,
} from 'app/services/api';
import _ from 'lodash';

import { LOAN_APPLICATION_ACTION_NAME_PREFIX } from 'constants/loanApplication';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { selectMortgageLiabilityOptions } from '../selectors';

import { LIABILITIES_ACTION_TYPE } from './liabilities.actions';

export const FINANCIAL_INFO_ACTION_TYPE = createNamedActions(
  // LOAN_APPLICATION_ACTION_NAME_PREFIX is used for Redux Middleware in Loan Editor
  `${LOAN_APPLICATION_ACTION_NAME_PREFIX}/financialInfo`,
  {
    GET_SUMMARY: 'GET_SUMMARY',
    GET_ASSETS: 'GET_ASSETS',
    GET_LIABILITIES: 'GET_LIABILITIES',
    GET_INCOMES: 'GET_INCOMES',
    GET_PROPERTIES: 'GET_PROPERTIES',
    UPDATE_RECORDS: 'UPDATE_RECORDS',
    TOGGLE_IS_CLOSED_HIDDEN: 'TOGGLE_IS_CLOSED_HIDDEN',
  },
);
export const getFinancialInformationSummaryAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await financialInfoApi.getSummary(params);

    return dispatch({
      type: FINANCIAL_INFO_ACTION_TYPE.GET_SUMMARY,
      payload: data,
    });
  },
);

export const getAssetsAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await financialInfoApi.getAssets(params);

  return dispatch({
    type: FINANCIAL_INFO_ACTION_TYPE.GET_ASSETS,
    payload: data,
  });
});

export const getLiabilitiesAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await financialInfoApi.getLiabilities(params);

  return dispatch({
    type: FINANCIAL_INFO_ACTION_TYPE.GET_LIABILITIES,
    payload: data,
  });
});

export const getIncomesAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await financialInfoApi.getIncomes(params);

  return dispatch({
    type: FINANCIAL_INFO_ACTION_TYPE.GET_INCOMES,
    payload: data,
  });
});

export const getFinancialInfoPropertiesAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await financialInfoApi.getProperties(params);

  return dispatch({
    type: FINANCIAL_INFO_ACTION_TYPE.GET_PROPERTIES,
    payload: data,
  });
});

export const updateIncomesAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await applicationIncomeApi.updateIncomes(params);

  return dispatch({
    type: FINANCIAL_INFO_ACTION_TYPE.UPDATE_RECORDS,
    payload: data,
  });
});

export const updateLiabilitiesAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await applicationLiabilityApi.updateLiabilities(params);

  return dispatch({
    type: FINANCIAL_INFO_ACTION_TYPE.UPDATE_RECORDS,
    payload: _.omit(data, params.isMortgageClicked ? ['metadata'] : []),
  });
});

export const updateAssetsAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await applicationLiabilityApi.updateAssets(params);

  return dispatch({
    type: FINANCIAL_INFO_ACTION_TYPE.UPDATE_RECORDS,
    payload: data,
  });
});

export const getBorrowersIncomesAction = catchAsyncActionError(({ applicationId }) => async () => {
  const { data } = await loanApplicationBorrowersApi.getBorrowersInfo({
    applicationId,
    params: { includes: 'incomes' },
  });

  return _.keyBy(_.flatMap(data, 'incomes').filter(Boolean), 'id');
});

export const getBorrowersLiabilitiesAction = catchAsyncActionError(
  ({ applicationId }) =>
    async () => {
      const { data } = await loanApplicationBorrowersApi.getBorrowersInfo({
        applicationId,
        params: { includes: 'liabilities' },
      });

      const formattedLiabilities = data.reduce((acc, { liabilities = [], id: borrowerId }) => {
        liabilities.forEach(liability => {
          acc[liability.id] = {
            ...liability,
            owners: [...new Set([...(acc[liability.id]?.owners ?? []), borrowerId])],
          };
        });

        return acc;
      }, {});

      return formattedLiabilities;
    },
);

export const getBorrowersAssetsAction = catchAsyncActionError(({ applicationId }) => async () => {
  const { data } = await loanApplicationBorrowersApi.getBorrowersInfo({
    applicationId,
    params: { includes: 'assets' },
  });

  return _.keyBy(_.flatMap(data, 'assets').filter(Boolean), 'id');
});

export const getBorrowersReoAction = catchAsyncActionError(params => async (dispatch, getState) => {
  const { data } = await loanPropertyApi.getProperties(params);

  const liabilitiesData = await dispatch(getBorrowersLiabilitiesAction(params));

  const normalizedLiabilities = Object.values(liabilitiesData).reduce(
    (acc, { liabilityAccountInfo, toBePaidOff, isPaymentExcluded, ...restLiability }) => ({
      ...acc,
      [restLiability.id]: {
        ...restLiability,
        ...liabilityAccountInfo,
        toBePaidOffAtOrBeforeClosing: toBePaidOff,
        excludePayment: isPaymentExcluded,
      },
    }),
    {},
  );

  dispatch({
    type: LIABILITIES_ACTION_TYPE.GET_LIABILITIES,
    payload: _.omit(normalizedLiabilities, params.isMortgageClicked ? ['metadata'] : []),
  });

  const state = getState();

  const liabilityOptions = selectMortgageLiabilityOptions()(state);

  return data.reduce((acc, curProperty) => {
    acc[curProperty.id] = {
      ...curProperty,
      liabilityOptions,
    };

    return acc;
  }, {});
});

export const updateReoAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await applicationPropertyApi.updateProperties(params);

  return dispatch({
    type: FINANCIAL_INFO_ACTION_TYPE.UPDATE_RECORDS,
    payload: data,
  });
});

export const toggleIsClosedHiddenAction = () => ({
  type: FINANCIAL_INFO_ACTION_TYPE.TOGGLE_IS_CLOSED_HIDDEN,
});
